import React from "react"
import cx from "classnames"

// =============================================================================
// Root
// =============================================================================

export type AppLayoutProps = {
  /** The navigation sidebar followed by an `AppLayout.Content`. */
  children?: React.ReactNode
  className?: string
}

/**
 * Top-level app shell. A full-height flex row that lays out the navigation
 * sidebar beside the main content card.
 *
 * Self-sufficient: it fills its width, establishes its own viewport height
 * (`h-svh`), and paints the app background, so it doesn't depend on an ancestor
 * to size or colour it. The navigation sidebar is passed as the first child (it
 * owns its own `<nav>` landmark) and the page content goes inside
 * `AppLayout.Content`.
 *
 * @example
 * import { AppLayout } from "@kit/design/app-layout"
 *
 * <AppLayout>
 *   <AppNavigation mobile={isMobile} />
 *   <AppLayout.Content mobile={isMobile} mobileNavExpanded={isMobile && navExpanded}>
 *     <Page>...</Page>
 *   </AppLayout.Content>
 * </AppLayout>
 */
function AppLayoutRoot({ children, className }: AppLayoutProps) {
  return (
    <div
      className={cx(
        "relative flex w-full h-svh overflow-hidden bg-warm-white-100",
        className
      )}
    >
      {children}
    </div>
  )
}

AppLayoutRoot.displayName = "AppLayout"

// =============================================================================
// Content
// =============================================================================

export type AppLayoutContentProps = {
  /** Main content — typically a `Page`, optionally preceded by banners. */
  children?: React.ReactNode
  className?: string
  /**
   * Pass `true` when the sidebar is in mobile mode (slides in/out rather than
   * collapsing to icon-only). Prevents the content from shrinking when the
   * sidebar opens.
   */
  mobile?: boolean
  /**
   * Pass `true` when the mobile nav is expanded (drawer is open over the
   * content). Adds `inert` so the content is non-interactive, and keeps frame
   * styling (margins, border, border-radius) so the content peeks behind the
   * drawer. When `false` or omitted, removes frame styling so the content sits
   * flush with the viewport. Animates with the sidebar's 250ms ease-out.
   */
  mobileNavExpanded?: boolean
}

/**
 * The content card beside the navigation. Owns the vertical scroll for the
 * page that lives inside it. The `<main>` landmark lives deeper, on `PageBody`,
 * so this is a plain `<div>`.
 */
function AppLayoutContent({
  children,
  className,
  mobile,
  mobileNavExpanded
}: AppLayoutContentProps) {
  return (
    <div
      className={cx(
        "flex-1 bg-white overflow-y-auto isolate transition-[margin] duration-[250ms] ease-out",
        (!mobile || mobileNavExpanded) &&
          "my-2 mr-2 rounded-[10px] border border-gray-200",
        mobile && "shrink-0 basis-full pb-20",
        className
      )}
      {...(mobileNavExpanded ? { inert: "" } : {})}
    >
      {children}
    </div>
  )
}

AppLayoutContent.displayName = "AppLayout.Content"

// =============================================================================
// Component
// =============================================================================

export const AppLayout = Object.assign(AppLayoutRoot, {
  Content: AppLayoutContent
})

export default AppLayout
